/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { useFormik } from 'formik';
import { memo, useRef, useState } from 'react';
import * as Yup from 'yup';

import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { FormControl, FormHelperText, IconButton, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import { notifyReportableApiError } from '../../../../components/notifyReportableApiError';

const useStyles = makeStyles(() => ({
  input: {
    '& *': {
      cursor: 'pointer',
      padding: 0,
    },
    '& svg': {
      visibility: 'hidden',
    },
    '&:hover svg': {
      visibility: 'visible',
    },
  },
}));

type Props = {
  readOnly: boolean;
  onSubmit: (value: string | null) => Promise<unknown>;
  value: string | undefined | null;
  placeholder?: string;
  schema: Yup.StringSchema<string | null>;
  'data-cy'?: string;
  className?: string;
};

export const EditableTypography = memo<Props>(props => {
  const classes = useStyles();

  const inputRef = useRef<{ blur: () => void; focus: () => void; selectionStart: number } | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  const formik = useFormik<{ value: string }>({
    enableReinitialize: true,
    initialValues: { value: props.value ?? '' },
    validationSchema: Yup.object({ value: props.schema }),
    onSubmit: value => {
      props
        .onSubmit(value.value || null)
        .then(() => {
          // Short timeout to give react some time to rerender with the the new values, otherwise there will be a flicker
          setTimeout(() => inputRef.current?.blur(), 100);
        })
        .catch(error => {
          notifyReportableApiError(error).catch(error => {
            console.log('Failed to notify reportable api:', error);
          });
        });
    },
    validateOnChange: true,
  });

  const hasError = !!formik.errors.value;

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormControl style={{ width: '100%' }} variant="outlined" error={hasError} className={classes.input}>
        <TextField
          inputRef={inputRef}
          id="value"
          name="value"
          fullWidth
          type="text"
          variant="standard"
          value={formik.values.value}
          data-cy={props['data-cy']}
          placeholder={props.placeholder}
          onChange={formik.handleChange}
          onFocus={() => !props.readOnly && setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            formik.resetForm();
          }}
          multiline={true}
          error={hasError}
          spellCheck={false}
          InputProps={{
            classes: { input: props.className },
            readOnly: props.readOnly,
            disableUnderline: !isFocused,
            endAdornment:
              !props.readOnly &&
              (isFocused ? (
                <IconButton
                  onMouseDown={e => {
                    e.preventDefault();
                    void formik.submitForm();
                  }}
                  sx={{ padding: 0 }}
                  size="small"
                >
                  <SaveIcon />
                </IconButton>
              ) : (
                <IconButton
                  onClick={() => {
                    if (inputRef.current) {
                      inputRef.current.focus();
                      inputRef.current.selectionStart = props.value?.length ?? 0;
                    }
                  }}
                  sx={{ padding: 0 }}
                  size="small"
                >
                  <EditIcon />
                </IconButton>
              )),
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void formik.submitForm();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              inputRef.current?.blur();
            }
          }}
        />
        {hasError && <FormHelperText style={{ marginLeft: 0 }}>{formik.errors.value}</FormHelperText>}
      </FormControl>
    </form>
  );
});
